import { IconChevronDown, IconFilterX, IconSearch } from "@tabler/icons-react";
import { Moment } from "moment-jalaali";
import {
  Children,
  ComponentProps,
  cloneElement,
  isValidElement,
  useState,
} from "react";
import { __ } from "@common/helpers/i18n";
import { PageFilterProps } from "@common/types";
import { useDocumentDirection } from "@app/hooks";
import { cn } from "@app/lib/utils";
import { Button, DatePicker, TextInput } from "../form";

export const pageControlClassName =
  "nh-page-control nh-filter-control min-w-0 shadow-none [&_button]:shadow-none [&_input]:shadow-none";

export const pageControlButtonClassName =
  "h-11 border-border bg-card text-sm font-medium leading-6 text-muted-foreground shadow-none hover:border-foreground/20 hover:bg-muted hover:text-foreground";

export function PageFilter({
  children,
  className,
  primaryItems = 3,
  surface = "plain",
}: PageFilterProps) {
  const direction = useDocumentDirection();
  const [filterToggle, setFilterToggle] = useState(false);

  const items = Children.toArray(children);
  const visibleItems = filterToggle ? items : items.slice(0, primaryItems);
  const advanced = items.slice(primaryItems);

  return (
    <ul
      data-page-filter="true"
      className={cn(
        "relative mb-0 grid w-full min-w-36 items-stretch gap-2 overflow-visible p-0 sm:flex sm:min-w-0 sm:flex-wrap sm:items-center sm:gap-2.5",
        items.length === 1 ? "grid-cols-1" : "grid-cols-2",
        "sm:justify-start",
        surface === "card" &&
          "rounded-lg border border-border bg-card/80 p-2 shadow-soft",
        "[&_button]:shadow-none [&_input]:shadow-none",
        className,
      )}
      aria-label={__("Filters")}
      dir={direction}
    >
      {visibleItems.map((child, index) => {
        const isButton = isValidElement(child) && child.type === Button;
        const filterChild =
          isValidElement<{ className?: string }>(child) && !isButton
            ? cloneElement(child, {
                className: cn(pageControlClassName, child.props.className),
              })
            : child;

        return (
          <li
            key={index}
            data-page-filter-item="true"
            className={cn(
              "group relative flex h-11 min-w-0 items-stretch justify-start font-medium [&>*]:h-11 [&>*]:min-w-0",
              isButton
                ? "[&>*]:w-full sm:w-auto sm:flex-none sm:[&>*]:w-auto"
                : "[&>*]:w-full sm:w-[208px] sm:flex-none",
            )}
          >
            {filterChild}
          </li>
        );
      })}
      {advanced.length !== 0 && (
        <li
          data-page-filter-item="true"
          className="flex h-11 min-w-0 [&>*]:w-full sm:w-[208px] sm:flex-none sm:[&>*]:w-full"
        >
          <Button
            type="button"
            variant="outline-light"
            fullWidth
            className={cn(
              pageControlButtonClassName,
              "nh-filter-control justify-between gap-2 px-3 text-start [&_svg]:h-[18px] [&_svg]:w-[18px]",
            )}
            onClick={() => setFilterToggle(!filterToggle)}
            title={__("Filters")}
          >
            <span className="min-w-0 flex-1 truncate text-start">
              {filterToggle ? __("Less filters") : __("More filters")}
            </span>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
              {filterToggle ? (
                <IconFilterX size={18} stroke={2} aria-hidden="true" />
              ) : (
                <IconChevronDown size={18} stroke={2} aria-hidden="true" />
              )}
            </span>
          </Button>
        </li>
      )}
    </ul>
  );
}

export function PageSearch({
  className,
  icon = <IconSearch size={18} stroke={2} />,
  placeholder,
  ...props
}: Omit<ComponentProps<typeof TextInput>, "icon"> & {
  icon?: ComponentProps<typeof TextInput>["icon"];
}) {
  return (
    <TextInput
      icon={icon}
      placeholder={placeholder || __("Search")}
      className={cn(pageControlClassName, className)}
      {...props}
    />
  );
}

export function PageDateRangeFilter({
  activeLabel,
  className,
  defaultMonth,
  from,
  max,
  min,
  onFromChange,
  onRangeChange,
  onReset,
  onToChange,
  placeholder,
  showIcon = false,
  to,
}: {
  activeLabel?: string;
  className?: string;
  defaultMonth?: Moment;
  from?: Moment;
  max?: Moment;
  min?: Moment;
  onFromChange?: (date: Moment) => void;
  onRangeChange?: (range: { from?: Moment; to?: Moment }) => void;
  onReset?: () => void;
  onToChange?: (date: Moment) => void;
  placeholder?: string;
  showIcon?: boolean;
  to?: Moment;
}) {
  const direction = useDocumentDirection();
  const hasRange = Boolean(from || to);
  const isSameDate = (left?: Moment, right?: Moment) => {
    if (!left && !right) {
      return true;
    }

    if (!left || !right) {
      return false;
    }

    return left.isSame(right);
  };

  return (
    <div
      className={cn(pageControlClassName, "block h-full w-full", className)}
      dir={direction}
    >
      <DatePicker
        range
        value={[from, to]}
        onChange={([dateFrom, dateTo]) => {
          const nextFrom =
            dateFrom && dateTo && dateFrom.isAfter(dateTo) ? dateTo : dateFrom;
          const nextTo =
            dateFrom && dateTo && dateFrom.isAfter(dateTo) ? dateFrom : dateTo;

          if (isSameDate(nextFrom, from) && isSameDate(nextTo, to)) {
            return;
          }

          if (onRangeChange) {
            onRangeChange({ from: nextFrom, to: nextTo });

            return;
          }

          if (nextFrom && !isSameDate(nextFrom, from)) {
            onFromChange?.(nextFrom);
          }

          if (nextTo && !isSameDate(nextTo, to)) {
            onToChange?.(nextTo);
          }
        }}
        placeholder={activeLabel || placeholder || __("Date range")}
        defaultMonth={defaultMonth}
        min={min}
        max={max}
        showIcon={showIcon}
        showChevron
        clearLabel={__("Clear filter")}
        onClear={hasRange ? onReset : undefined}
        className={cn(
          pageControlButtonClassName,
          "app-date-picker__field--compact h-10 w-full justify-between gap-2 md:h-11",
          hasRange &&
            "border-primary/25 bg-primary/5 text-primary hover:border-primary/35 hover:bg-primary/10",
        )}
      />
    </div>
  );
}
